import {Component, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {ToastService} from "../../services/Toast.service";
import {SwitchBoard} from "../../services/SwitchBoard";
import {Router} from "@angular/router";
import {LoginService} from "../../services/Login.service";
import {LoadingService} from "../../services/LoadingService";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {TaxPlanningService} from "../../tpiq-services/tax-planning.service";
import {Session} from "../../services/Session";
import {CommonModule} from "@angular/common";

@Component({
  selector: 'app-auto-login',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './auto-login.component.html',
  styleUrl: './auto-login.component.scss'
})
export class AutoLoginComponent implements OnInit, OnDestroy {
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  loginCredentials: any;
  userId: any;
  org: any;
  token: any;
  isLoginFailed: boolean = false;
  errorMessage: any;

  constructor(
    private _router: Router,
    private loadingService: LoadingService,
    private loginService: LoginService,
    private taxPlanningService: TaxPlanningService,
    private _toastService: ToastService,
    private switchBoard: SwitchBoard
  ) {
  }

  ngOnInit() {
    this.loginCredentials = Session.get('signupData');
    const cardInfo = Session.get('cardTransactionInfo');
    let obj = {
      id       : this.loginCredentials.email,
      password : this.loginCredentials.desiredPassword,
      rememberMe : false,
      username : this.loginCredentials.email,
      org      : cardInfo?.orgId || ''
    };
    if (obj.id && obj.password) {
      this.loadingService.triggerLoadingEvent(true);
      this.loginService.login(obj)
        .subscribe({
          next:  (success) => { this.showMessage(true, success); },
          error: (error)   => { this.showMessage(false, error); },
        });
    } else {
      this.isLoginFailed = true;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showMessage(status, obj) {
    if (status) {
      this.addSessionData(obj);
    } else {
      this.isLoginFailed = true;
      this.errorMessage = 'Something went wrong.';
      if (obj && obj.message){
        this.errorMessage = obj.message;
      }
      this.loadingService.triggerLoadingEvent(false);
    }
  }

  addSessionData(obj) {
    // CU-868kpjgur: mark the attempt before the requests below run. Another
    // tab must not treat this credential as an abandoned login and clear it.
    Session.markLoginAttempt();
    Session.put('userId', obj.userId);
    Session.setToken(obj.token);
    Session.setRefreshToken(obj.refreshToken);
    Session.setOrg(obj.org);
    this.userId = obj.userId;
    this.org = obj.org;
    this.token = obj.token;
    this.getUserSubscriptionAccessList(this.userId, this.org, obj.token);
  }

  getUserSubscriptionAccessList(userId, org, attemptToken?) {
    this.taxPlanningService.getSubscriptionStatus(org, userId).subscribe({
      next: (response) => {
        let obj = {};
        obj['clientPortalAccess'] = false;
        obj['dashboardAccess'] = false;
        obj['subscriptionStatus'] = response.subscriptionStatus;
        // The header logo reads productAccess (same field as login.component.ts).
        obj['productAccess'] = response.productAccess;
        if (response.accessList && response.accessList.length > 0) {
          obj['dashboardAccess'] = response.accessList.indexOf('Dashboard') != -1;
          obj['clientPortalAccess'] = response.accessList.indexOf('Client Portal') != -1;
        }
        Session.setUserAccessInfo(obj);
        this.switchBoard.onUserAccess.next({});
        this.getUserInfo(userId, org);
      },
      error: () => {
        // Act only while this attempt still owns the session (CodeRabbit, PR
        // #765). Nothing cancels this request. So a newer login can finish
        // first. An old attempt must not clear the session that replaced it.
        if (attemptToken && Session.getToken() !== attemptToken) {
          return;
        }
        this._toastService.pop(TOAST_TYPE.error, 'Failed to get subscription status');
        // CU-868kpjgur: this repeats the fault in login.component.
        // addSessionData() above wrote the credential. This call writes `user`
        // only when it succeeds. A half-written session makes the next page
        // load show a blank page.
        Session.destroy();
        this.loadingService.triggerLoadingEvent(false);
        // Without this line the page keeps the "please wait" text on screen.
        // The template then shows a general error message.
        this.isLoginFailed = true;
      },
    });
  }

  getUserInfo(userId, org){
    this.loginService.getUserCompanyInfo(userId, org).subscribe({
      next:  (data) => { this.updateSessionDetails(data); },
      error: ()     => { this.switchBoard.onLogOut.next({}); },
    });
  }

  updateSessionDetails(obj) {
    Session.put('user', obj);
    Session.setCurrentCompanyCurrency('USD');
    this.loadingService.triggerLoadingEvent(false);
    const accessInfo: any = Session.getUserAccessInfo();
    if (Session.get('user').tempPassword) {
      this._router.navigate(['activate']);
    } else if (accessInfo && accessInfo.subscriptionStatus == 'Suspended') {
      this._router.navigate(['tax-planning/manage-billing']);
    } else if (obj.onboardingStatus !== 'Done') {
      this._router.navigate(['tpiq/onboard']);
    } else {
      // Fire onLogin so AppComponent sets hasLoggedIn=true and user (renders sidebar+header),
      // then defer navigation one tick so Angular CD can render the shell first.
      this.switchBoard.onLogin.next(Session.getUser('delegatedFrom'));
      const homeobj = { type: 'taxPlanningHome', route: 'taxPlanning', clearAllStates: true };
      setTimeout(() => {
        this.switchBoard.onWallTpiqSwitch.next(homeobj);
      });
    }
  }

}
